import { InputActions } from "@common/constants";
import { Ammos } from "@common/definitions/items/ammos";
import { ArmorType, type ArmorDefinition } from "@common/definitions/items/armors";
import { type BackpackDefinition } from "@common/definitions/items/backpacks";
import { HealingItems } from "@common/definitions/items/healingItems";
import { Scopes, type ScopeDefinition } from "@common/definitions/items/scopes";
import { Throwables } from "@common/definitions/items/throwables";
import { Loots, type LootDefinition } from "@common/definitions/loots";
import { bagFill, spillsOver, type BagFill } from "@common/utils/bagFill";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { maxScopeCopies, type SpareKind } from "@common/utils/spareGear";
import { Game } from "../game";
import { holdAutoPickup } from "../utils/autoPickupHold";
import { sentences } from "../utils/translations/phrases";
import { translate } from "../utils/translations/translations";
import { type TranslationKeys } from "../utils/translations/typings";
import { InputManager } from "./inputManager";
import { UIManager } from "./uiManager";

/**
 * The inventory screen (owner request of 16 October 2026, from a tester: "an inventory screen for loot management could
 * be helpful, something on the hud which also shows backpack usage %"; the owner then asked for it "more like Escape from
 * Tarkov but much more streamlined"). Tab or I (`toggle_inventory`) opens it over the game: the equipment slots on the
 * left, each with the spare carried beside it (common/src/utils/spareGear.ts), the weapon slots under them, and on the
 * right the backpack as a grid of squares (more of them the better the backpack), a stack of one kind of item in each,
 * its count and how full it is (ammo, wood and healing items go on into the next empty square once a stack is full:
 * common/src/utils/bagFill.ts), then the scopes. An item dragged out of the panel and let go is dropped
 * (the owner: "does grab and sliding it outside the inventory drop it? Make sure it does"). Clicking an item shows it in
 * the strip at the bottom with its Use and Drop buttons; a right-click drops it straight away, a double click uses it.
 * Beside the backpack on the HUD, how full the bag is (common/src/utils/bagFill.ts); clicking or tapping it opens the
 * screen too (the way in on phones, where it's laid out in three columns on one screen: client/index.html). Moving still
 * works with the screen open; clicks on it never reach the game, and a click outside closes it.
 */

const esc = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const element = <K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] => {
    const created = document.createElement(tag);
    if (className) created.className = className;
    if (text) created.textContent = text;
    return created;
};
/** the game's own words, by their key (`t` is the menu's phrases', utils/translations/phrases.ts) */
const tk =(key: string, replacements?: Record<string, string>): string => translate(key as TranslationKeys, replacements);

function nameOf(definition: LootDefinition): string {
    if (definition.defType === DefinitionType.Gun && definition.isDual) {
        return translate("dual_template", { gun: translate(definition.singleVariant as TranslationKeys) });
    }
    return translate(("translationString" in definition ? definition.translationString : definition.idString) as TranslationKeys);
}

function imageOf(definition: LootDefinition): string {
    switch (definition.defType) {
        case DefinitionType.Gun:
        case DefinitionType.Melee:
        case DefinitionType.Throwable:
            return definition.idString === "fists" ? "./img/hud/fist.svg" : `./img/game/weapons/${definition.idString}.svg?v=${ASSET_STAMP}`;
        default:
            return `./img/game/loot/${definition.idString}.svg`;
    }
}

/**
 * A press: the pointer goes down and up again on the same element (a click, a tap). Phones get no click event here
 * (the touch controls cancel the touches), so presses are read from the pointer events, which they get
 */
function onPress(target: HTMLElement, pick: (event: PointerEvent) => HTMLElement | null, run: (element: HTMLElement) => void): void {
    let down: { readonly id: number, readonly element: HTMLElement } | undefined;
    target.addEventListener("pointerdown", (event) => {
        const picked = event.button === 0 ? pick(event) : null;
        down = picked ? { id: event.pointerId, element: picked } : undefined;
    });
    target.addEventListener("pointerup", (event) => {
        const pressed = down;
        down = undefined;
        if (pressed?.id !== event.pointerId || pick(event) !== pressed.element) return;
        run(pressed.element);
    });
    target.addEventListener("pointercancel", () => { down = undefined; });
}

type Action =
    | { readonly kind: "drop", readonly item: LootDefinition }
    | { readonly kind: "drop-weapon", readonly slot: number }
    | { readonly kind: "use", readonly item: LootDefinition };

/** what the strip at the bottom shows for an item picked on the screen, and what can be done with it */
interface Entry {
    readonly definition: LootDefinition
    readonly info: string
    readonly use?: Action
    readonly drop?: Action
    /** a stack: Drop takes half, or (more than a square of ammo, wood or a healing item) a square */
    readonly stack?: "half" | "square"
}

/** how far (in pixels) an item moves held down before it's dragged rather than clicked */
const DRAG_START = 6;

/** a tile's tint, by what it is (an ammo's own colour), as in a stash */
function tintOf(definition: LootDefinition): string {
    switch (definition.defType) {
        case DefinitionType.Ammo: {
            const { hue, saturation } = definition.characteristicColor;
            return `hsla(${hue}, ${Math.min(55, saturation)}%, 38%, 0.24)`;
        }
        case DefinitionType.HealingItem: return "rgba(150, 64, 50, 0.24)";
        case DefinitionType.Throwable: return "rgba(108, 120, 70, 0.24)";
        case DefinitionType.Scope: return "rgba(88, 108, 128, 0.22)";
        default: return "rgba(255, 255, 255, 0.04)";
    }
}

class InventoryScreenClass {
    private _root?: HTMLDivElement;
    private _panel?: HTMLDivElement;
    private _meter?: HTMLButtonElement;
    private _meterValue?: HTMLElement;
    private _meterBar?: HTMLElement;
    private _open = false;
    private _signature = "";
    private _meterKey = "";
    private readonly _actions: Action[] = [];
    /** what's on the screen, by key ("gear:helmet", "spare:vest", "weapon:0", "item:gauze", "scope:4x_scope") */
    private readonly _entries = new Map<string, Entry>();
    /** the item picked, shown in the strip at the bottom */
    private _selected?: string;
    private _lastPress?: { readonly key: string, readonly at: number };
    /** an item held down: a click when let go where it was, a drag once moved (let go outside the panel, it's dropped) */
    private _press?: { readonly key: string, readonly pointerId: number, readonly x: number, readonly y: number, dragging: boolean, out: boolean };
    private _ghost?: HTMLDivElement;

    get isOpen(): boolean { return this._open; }

    private readonly _onKey = (event: KeyboardEvent): void => {
        if (event.key !== "Escape" || !this._open) return;
        // closes the screen, and only that (no game menu)
        event.preventDefault();
        event.stopPropagation();
        this.close();
    };

    private _build(): void {
        if (this._root) return;
        if (!document.getElementById("game-ui")) return;

        const root = element("div");
        root.id = "inventory-screen";
        root.hidden = true;
        const backdrop = element("div", "inv-backdrop");
        const panel = element("div", "inv-panel");
        panel.setAttribute("role", "dialog");
        panel.setAttribute("aria-label", tk("inventory_title"));
        root.append(backdrop, panel);

        // nothing here reaches the game: no shot fired by a click on the screen
        for (const type of ["pointerdown", "pointerup", "mousedown", "mouseup", "click", "contextmenu", "wheel", "touchstart", "touchmove", "touchend"]) {
            root.addEventListener(type, (event) => {
                event.stopPropagation();
                // a button never takes the keyboard's focus (the game ignores keys while something else has it)
                if (type === "mousedown" || type === "contextmenu") event.preventDefault();
            });
        }
        onPress(backdrop, (event) => (event.target === backdrop ? backdrop : null), () => this.close());
        // the buttons, and a press on empty space (it puts the item picked down)
        onPress(panel, (event) => {
            const target = event.target as HTMLElement;
            return target.closest("[data-key]") ? null : target.closest<HTMLElement>("button[data-act], .tk-main");
        }, (picked) => {
            if (picked.dataset.act === undefined) {
                this._select(undefined);
                return;
            }
            picked.blur();
            if (picked.dataset.act === "close") {
                this.close();
                return;
            }
            const action = this._actions[Number(picked.dataset.act)];
            if (action) this._run(action);
        });
        // the items: a click picks one (a double click uses it), a drag out of the panel drops it
        panel.addEventListener("pointerdown", (event) => {
            // (a second finger while one drags is ignored)
            if (event.button !== 0 || this._press?.dragging) return;
            const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset.key;
            if (key === undefined || !this._entries.has(key)) return;
            this._press = { key, pointerId: event.pointerId, x: event.clientX, y: event.clientY, dragging: false, out: false };
        });
        root.addEventListener("pointermove", (event) => {
            const press = this._press;
            if (press?.pointerId !== event.pointerId) return;
            if (!press.dragging) {
                if (Math.hypot(event.clientX - press.x, event.clientY - press.y) < DRAG_START) return;
                const entry = this._entries.get(press.key);
                // only what can be dropped is dragged (not fists)
                if (!entry?.drop) {
                    this._press = undefined;
                    return;
                }
                press.dragging = true;
                // the rest of the drag comes here wherever the pointer goes (off the window too, and when the screen
                // is redrawn under it)
                try { root.setPointerCapture(event.pointerId); } catch { /* the pointer is already gone */ }
                this._startDrag(press.key, entry);
            } else if (event.pointerType === "mouse" && event.buttons === 0) {
                // the button came up where the page never heard it: the drag is called off
                this._cancelDrag();
                return;
            }
            this._moveDrag(press, event);
        });
        root.addEventListener("pointerup", (event) => {
            const press = this._press;
            if (press?.pointerId !== event.pointerId) return;
            this._press = undefined;
            if (press.dragging) {
                this._endDrag(press);
                return;
            }
            // let go on the item it was pressed on: a click
            const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset.key;
            if (key === press.key) this._click(key);
        });
        root.addEventListener("pointercancel", (event) => {
            if (this._press?.pointerId === event.pointerId) this._cancelDrag();
        });
        // a right-click drops it straight away (as on the HUD's slots); a finger held down on a phone isn't one
        panel.addEventListener("contextmenu", (event) => {
            const pointerType = (event as Partial<PointerEvent>).pointerType;
            if (InputManager.isMobile || (pointerType !== undefined && pointerType !== "" && pointerType !== "mouse")) return;
            const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset.key;
            const drop = key === undefined ? undefined : this._entries.get(key)?.drop;
            if (drop) this._run(drop);
        });

        // over everything in the game, the Training Range's bar too (outside the game's own layers)
        document.body.appendChild(root);
        this._root = root;
        this._panel = panel;

        // the bag meter beside the backpack (and the way in on phones)
        const meter = element("button");
        meter.type = "button";
        meter.id = "bag-meter";
        meter.tabIndex = -1;
        const value = element("span", "bm-v", "0%");
        const bar = element("i");
        const track = element("span", "bm-bar");
        track.append(bar);
        meter.append(element("i", "fa-solid fa-suitcase"), element("span", "bm-k", tk("inventory_bag").toUpperCase()), value, track);
        for (const type of ["pointerdown", "pointerup", "mousedown", "mouseup", "click"]) {
            meter.addEventListener(type, (event) => {
                event.stopPropagation();
                if (type === "mousedown") event.preventDefault();
            });
        }
        onPress(meter, () => meter, () => {
            meter.blur();
            this.toggle();
        });
        const backpack = document.getElementById("backpack-slot");
        if (backpack) backpack.after(meter);
        else document.getElementById("equipment-container")?.appendChild(meter);
        this._meter = meter;
        this._meterValue = value;
        this._meterBar = bar;
    }

    /** whether the screen can be used now: playing (not watching, not dead) */
    private get _usable(): boolean {
        return Game.gameStarted && !Game.gameOver && !Game.spectating && Game.activePlayer !== undefined && !Game.activePlayer.dead;
    }

    toggle(): void {
        if (this._open) this.close();
        else this.open();
    }

    open(): void {
        this._build();
        if (!this._root || !this._usable) return;
        this._open = true;
        this._signature = "";
        this._selected = undefined;
        this._root.hidden = false;
        document.body.classList.add("inventory-open");
        window.addEventListener("keydown", this._onKey, true);
        this.refresh();
    }

    close(): void {
        if (!this._open) return;
        this._cancelDrag();
        this._open = false;
        if (this._root) this._root.hidden = true;
        document.body.classList.remove("inventory-open");
        window.removeEventListener("keydown", this._onKey, true);
    }

    /** a match ended or the player left it: the screen goes, the meter starts again */
    reset(): void {
        this.close();
        this._meterKey = "";
        this._signature = "";
        this._selected = undefined;
    }

    /** a click on an item: picks it; a second click on it straight away uses it (a healing item, a scope) */
    private _click(key: string): void {
        const now = performance.now();
        const again = this._lastPress?.key === key && now - this._lastPress.at < 350;
        this._lastPress = { key, at: now };
        const use = this._entries.get(key)?.use;
        if (again && use) this._run(use);
        this._select(key);
    }

    /** an item dragged: a copy of it follows the pointer, saying what letting go does */
    private _startDrag(key: string, entry: Entry): void {
        const root = this._root;
        if (!root) return;
        this._ghost?.remove();
        const ghost = element("div", "tk-ghost");
        ghost.style.setProperty("--tint", tintOf(entry.definition));
        const image = element("img");
        image.src = imageOf(entry.definition);
        image.alt = "";
        image.draggable = false;
        ghost.append(image, element("span", "tk-ghost-in", tk("inventory_drag_out")), element("span", "tk-ghost-out", tk("inventory_drop")));
        root.append(ghost);
        this._ghost = ghost;
        root.classList.add("dragging");
        this._panel?.querySelector(`[data-key="${CSS.escape(key)}"]`)?.classList.add("lifted");
    }

    private _moveDrag(press: NonNullable<InventoryScreenClass["_press"]>, event: PointerEvent): void {
        const ghost = this._ghost;
        const box = this._panel?.getBoundingClientRect();
        if (!ghost || !box) return;
        ghost.style.transform = `translate(${event.clientX}px, ${event.clientY}px)`;
        press.out = event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
        ghost.classList.toggle("out", press.out);
        this._root?.classList.toggle("drop-here", press.out);
    }

    /** let go: outside the panel the item is dropped, inside nothing happens */
    private _endDrag(press: NonNullable<InventoryScreenClass["_press"]>): void {
        const drop = press.out ? this._entries.get(press.key)?.drop : undefined;
        this._cancelDrag();
        if (drop) this._run(drop);
    }

    private _cancelDrag(): void {
        this._press = undefined;
        this._ghost?.remove();
        this._ghost = undefined;
        this._root?.classList.remove("dragging", "drop-here");
        this._panel?.querySelector(".lifted")?.classList.remove("lifted");
    }

    /** picks an item (or none): the strip at the bottom shows it */
    private _select(key: string | undefined): void {
        if (this._selected === key) return;
        this._selected = key;
        this._signature = "";
        this.refresh();
    }

    private _run(action: Action): void {
        if (!this._usable) return;
        const at = Game.activePlayer?.position;
        switch (action.kind) {
            case "drop":
                InputManager.addAction({ type: InputActions.DropItem, item: action.item as never });
                // a phone doesn't pick it straight back up while the player stands there
                holdAutoPickup(action.item.idString, at);
                break;
            case "drop-weapon": {
                const weapon = UIManager.inventory.weapons[action.slot]?.definition;
                InputManager.addAction({ type: InputActions.DropWeapon, slot: action.slot });
                if (weapon) holdAutoPickup(weapon.idString, at);
                break;
            }
            case "use": InputManager.addAction({ type: InputActions.UseItem, item: action.item as never }); break;
        }
    }

    /** the ammo of the guns held: counted in the bag's fill even with none of it */
    private _gunAmmo(): string[] {
        const ammo: string[] = [];
        for (const weapon of UIManager.inventory.weapons) {
            const definition = weapon?.definition;
            if (definition?.defType === DefinitionType.Gun && !ammo.includes(definition.ammoType)) ammo.push(definition.ammoType);
        }
        return ammo;
    }

    private _fill(): BagFill | undefined {
        const backpack = Game.activePlayer?.equipment.backpack;
        if (!backpack) return undefined;
        return bagFill(UIManager.inventory.items, backpack, this._gunAmmo());
    }

    /** after any change to what's carried: the HUD meter, and the screen when it's open */
    refresh(): void {
        this._build();
        if (this._open && !this._usable) this.close();
        const fill = this._fill();
        this._refreshMeter(fill);
        if (this._open && fill) this._render(fill);
    }

    private _refreshMeter(fill: BagFill | undefined): void {
        const meter = this._meter;
        if (!meter) return;
        const percent = fill?.percent ?? 0;
        const full = fill?.full ?? [];
        const key = `${percent}|${full.join(",")}`;
        if (key === this._meterKey) return;
        this._meterKey = key;
        if (this._meterValue) this._meterValue.textContent = `${percent}%`;
        if (this._meterBar) this._meterBar.style.width = `${percent}%`;
        meter.classList.toggle("full", percent >= 100);
        meter.classList.toggle("high", percent >= 80);
        const keys = InputManager.binds.getInputsBoundToAction("toggle_inventory").filter((key) => key !== "").slice(0, 2);
        const fullNames = full.map((id) => nameOf(Loots.fromString(id)));
        meter.title = [
            tk("inventory_bag_meter", { percent: String(percent) }),
            fullNames.length > 0 ? tk("inventory_full", { items: fullNames.join(", ") }) : "",
            keys.length > 0 ? tk("inventory_open_with", { keys: keys.join(" / ") }) : ""
        ].filter(Boolean).join("\n");
    }

    private _button(action: Action, label: string, style = ""): string {
        this._actions.push(action);
        return `<button type="button" tabindex="-1" class="tk-btn ${style}" data-act="${this._actions.length - 1}">${esc(label)}</button>`;
    }

    private _render(fill: BagFill): void {
        const panel = this._panel;
        const player = Game.activePlayer;
        if (!panel || !player) return;
        const { items, weapons, scope, spares } = UIManager.inventory;
        const equipment = player.equipment;

        const signature = JSON.stringify([
            equipment.helmet?.idString, equipment.vest?.idString, equipment.mask?.idString, equipment.backpack.idString,
            spares.helmet?.idString, spares.vest?.idString, spares.backpack?.idString,
            weapons.map((weapon) => [weapon?.definition.idString, weapon?.count]), scope.idString, items, fill.percent, this._selected
        ]);
        if (signature === this._signature) return;
        this._signature = signature;
        this._actions.length = 0;
        this._entries.clear();

        const selected = (key: string): string => (key === this._selected ? " selected" : "") + (this._press?.dragging && this._press.key === key ? " lifted" : "");

        // a slot: what's worn, a spare beside it, a weapon (wide)
        const slot = (key: string, label: string, definition: LootDefinition | undefined, extra: { readonly className?: string, readonly corner?: string } = {}): string => {
            const shown = definition !== undefined && !definition.hideInHUD && !(definition.defType === DefinitionType.Backpack && definition.level <= 0);
            const className = `tk-slot${extra.className ? ` ${extra.className}` : ""}${shown ? "" : " empty"}${shown ? selected(key) : ""}`;
            return `<div class="${className}"${shown ? ` data-key="${key}" title="${esc(nameOf(definition))}"` : ""}>`
                + `<span class="tk-label">${esc(label)}</span>`
                + (shown ? `<img src="${imageOf(definition)}" alt="" draggable="false"><span class="tk-name">${esc(nameOf(definition))}</span>` : "")
                + (shown && extra.corner ? `<span class="tk-corner">${esc(extra.corner)}</span>` : "")
                + "</div>";
        };

        // the equipment, each piece with the spare carried beside it
        const armorInfo = (definition: ArmorDefinition): string => definition.armorType === ArmorType.Mask
            ? sentences(tk("inventory_info_armor", { level: String(definition.level), percent: String(Math.round(definition.damageReduction * 100)) }), tk("inventory_info_mask", { percent: String(Math.round(definition.gasReduction * 100)) }))
            : tk("inventory_info_armor", { level: String(definition.level), percent: String(Math.round(definition.damageReduction * 100)) });
        const gearRow = (kind: SpareKind | "mask", label: string): string => {
            const worn: ArmorDefinition | BackpackDefinition | undefined = equipment[kind];
            if (worn && !worn.hideInHUD && worn.level > 0) {
                this._entries.set(`gear:${kind}`, {
                    definition: worn,
                    info: worn.defType === DefinitionType.Armor ? armorInfo(worn) : tk("inventory_info_backpack", { level: String(worn.level) }),
                    drop: worn.noDrop ? undefined : { kind: "drop", item: worn }
                });
            }
            const main = slot(`gear:${kind}`, label, worn, { corner: worn && worn.level > 0 ? String(worn.level) : undefined });
            if (kind === "mask") return `<div class="tk-gear-row">${main}</div>`;
            const spare = spares[kind];
            if (spare && worn) {
                this._entries.set(`spare:${kind}`, {
                    definition: spare,
                    info: sentences(tk("inventory_info_spare", { item: nameOf(worn) }), spare.defType === DefinitionType.Armor ? armorInfo(spare) : tk("inventory_info_backpack", { level: String(spare.level) })),
                    drop: { kind: "drop", item: spare }
                });
            }
            return `<div class="tk-gear-row">${main}${slot(`spare:${kind}`, tk("inventory_slot_spare"), spare, { className: "spare", corner: spare ? String(spare.level) : undefined })}</div>`;
        };
        const gear = [
            gearRow("helmet", tk("inventory_slot_headwear")),
            gearRow("vest", tk("inventory_slot_armor")),
            ...(equipment.mask ? [gearRow("mask", tk("inventory_slot_face"))] : []),
            gearRow("backpack", tk("inventory_slot_backpack"))
        ].join("");

        // the weapon slots: the guns, the melee weapon, the grenade in hand
        const weaponLabels = [tk("inventory_slot_primary"), tk("inventory_slot_secondary"), tk("inventory_slot_melee"), tk("inventory_slot_throwable")];
        const weaponSlots = weaponLabels.map((label, index) => {
            const weapon = weapons[index];
            const definition = weapon?.definition;
            if (definition) {
                const droppable = !definition.noDrop && definition.idString !== "fists";
                this._entries.set(`weapon:${index}`, {
                    definition,
                    info: sentences(tk("inventory_info_weapon", { slot: String(index + 1) }), weapon.count !== undefined && definition.defType === DefinitionType.Gun ? tk("inventory_info_loaded", { count: String(weapon.count) }) : ""),
                    drop: droppable ? { kind: "drop-weapon", slot: index } : undefined
                });
            }
            return slot(`weapon:${index}`, label, definition, { className: "wide", corner: weapon?.count !== undefined && definition?.defType !== DefinitionType.Melee ? String(weapon.count) : undefined });
        }).join("");

        // the backpack: its squares, a stack of one kind in each (ammo, wood and healing items go on into another square
        // once a stack is full), and the ammo of the guns held even with none of it, in a grid
        const tile = (key: string, definition: LootDefinition, count: number, share: number | undefined, flags: { readonly full?: boolean, readonly mark?: string, readonly hideCount?: boolean } = {}): string => {
            const meter = share === undefined ? "" : `<span class="tk-meter"><i style="width: ${Math.round(Math.min(1, share) * 100)}%"></i></span>`;
            return `<div class="tk-tile${flags.full ? " full" : ""}${count <= 0 ? " none" : ""}${selected(key)}" data-key="${key}" title="${esc(nameOf(definition))}" style="--tint: ${tintOf(definition)}">`
                + `<img src="${imageOf(definition)}" alt="" draggable="false">`
                + (flags.mark ? `<span class="tk-mark">${esc(flags.mark)}</span>` : "")
                + (flags.hideCount ? "" : `<span class="tk-qty">${count}</span>`) + `${meter}</div>`;
        };
        const kinds = new Map(fill.rooms.map((room) => [room.idString, room]));
        const order: LootDefinition[] = [
            ...HealingItems.definitions,
            ...Ammos.definitions.filter((ammo) => !ammo.ephemeral && ammo.idString !== "wood"),
            ...Throwables.definitions,
            ...Ammos.definitions.filter((ammo) => ammo.idString === "wood")
        ];
        const tiles: string[] = [];
        for (const definition of order) {
            const room = kinds.get(definition.idString);
            if (!room) continue;
            const carried = Math.max(0, room.count);
            // (the bag can't take another: its square full, and for ammo, wood and healing items no empty square left)
            const full = carried >= room.most;
            const spills = spillsOver(definition.idString);
            const counts = { count: String(carried), room: String(room.room) };
            const entry: Entry = {
                definition,
                info: spills
                    ? tk(full ? "inventory_info_squares_full" : "inventory_info_squares", counts)
                    : sentences(tk("inventory_info_bag", counts), full ? tk("inventory_info_full") : ""),
                use: definition.defType === DefinitionType.HealingItem && carried > 0 ? { kind: "use", item: definition } : undefined,
                drop: carried > 0 && !definition.noDrop ? { kind: "drop", item: definition } : undefined,
                stack: spills && carried > room.room ? "square" : carried > 1 ? "half" : undefined
            };
            // a tile a square: the full stacks, then what's in the last one
            const squares = Math.max(1, room.squares);
            for (let square = 0; square < squares; square++) {
                const key = square === 0 ? `item:${definition.idString}` : `item:${definition.idString}:${square}`;
                const inSquare = square < squares - 1 ? room.room : carried - (squares - 1) * room.room;
                this._entries.set(key, entry);
                tiles.push(tile(key, definition, inSquare, room.room > 0 ? inSquare / room.room : 0, { full }));
            }
        }
        // (the backpack's grid, common/src/utils/bagFill.ts: what the percent is of, its empty squares shown; more tiles
        // than squares, when kinds took their first squares in a bag already full, run on into a row of their own,
        // with no empty squares after them: the bag has none)
        const grid = tiles.join("") + "<div class=\"tk-void\"></div>".repeat(Math.max(0, fill.cells - tiles.length));

        // the scopes carried: the one in use marked, a second copy counted
        const scopeTiles = Scopes.definitions
            .filter((definition: ScopeDefinition) => (items[definition.idString] ?? 0) > 0)
            .map((definition: ScopeDefinition) => {
                const count = items[definition.idString] ?? 0;
                const inUse = scope.idString === definition.idString;
                const key = `scope:${definition.idString}`;
                this._entries.set(key, {
                    definition,
                    info: sentences(inUse ? tk("inventory_info_in_use") : "", maxScopeCopies(definition) > 1 ? tk("inventory_info_scope", { count: String(count), max: String(maxScopeCopies(definition)) }) : ""),
                    use: inUse ? undefined : { kind: "use", item: definition },
                    drop: definition.noDrop ? undefined : { kind: "drop", item: definition }
                });
                // (a count only for a second copy, as for a stack)
                return tile(key, definition, count, undefined, { mark: inUse ? "\u25CF" : undefined, hideCount: count <= 1 });
            }).join("");

        // the strip at the bottom: the item picked and what can be done with it, or how to pick one
        if (this._selected !== undefined && !this._entries.has(this._selected)) this._selected = undefined;
        const entry = this._selected === undefined ? undefined : this._entries.get(this._selected);
        const keys = InputManager.binds.getInputsBoundToAction("toggle_inventory").filter((key) => key !== "").slice(0, 2);
        // (a phone has no keys and no right-click: taps and drags)
        const touch = InputManager.isMobile;
        const closeHint = touch ? "" : `<span class="tk-keys">${esc(tk("inventory_close_hint", { keys: keys.join(" / ") || "Tab" }))}</span>`;
        const detail = entry
            ? `<div class="tk-detail">`
                + `<span class="tk-detail-pic" style="--tint: ${tintOf(entry.definition)}"><img src="${imageOf(entry.definition)}" alt=""></span>`
                + `<span class="tk-detail-text"><b>${esc(nameOf(entry.definition))}</b><span>${esc(entry.info)}</span></span>`
                + `<span class="tk-detail-actions">${entry.use ? this._button(entry.use, tk("inventory_use"), "use") : ""}${entry.drop ? this._button(entry.drop, entry.stack === "square" ? tk("inventory_drop_square") : entry.stack === "half" ? tk("inventory_drop_half") : tk("inventory_drop")) : ""}</span>`
                + "</div>"
            : `<div class="tk-detail idle"><span class="tk-hint">${esc(tk(touch ? "inventory_pick_hint_touch" : "inventory_pick_hint"))}</span>${closeHint}</div>`;

        const level = fill.percent >= 100 ? " full" : fill.percent >= 80 ? " high" : "";
        const backpackName = equipment.backpack.level > 0 ? nameOf(equipment.backpack) : tk("inventory_no_backpack");
        panel.innerHTML = `
            <div class="tk-top">
                <span class="tk-title">${esc(tk("inventory_title"))}</span>
                <button type="button" tabindex="-1" class="tk-close" data-act="close" aria-label="${esc(tk("inventory_close"))}">&times;</button>
            </div>
            <div class="tk-main">
                <div class="tk-col tk-left">
                    <section class="tk-sec tk-sec-gear">
                        <div class="tk-h"><span>${esc(tk("inventory_equipment"))}</span></div>
                        <div class="tk-gear">${gear}</div>
                    </section>
                    <section class="tk-sec tk-sec-weapons">
                        <div class="tk-h"><span>${esc(tk("inventory_weapons"))}</span></div>
                        <div class="tk-weapons">${weaponSlots}</div>
                    </section>
                </div>
                <div class="tk-col tk-right">
                    <section class="tk-sec tk-sec-bag">
                        <div class="tk-h${level}"><span>${esc(backpackName)}</span><span class="tk-pct" title="${esc(tk("inventory_bag_meter", { percent: String(fill.percent) }))}">${fill.percent}%<i>${esc(tk("inventory_full_word"))}</i></span></div>
                        <div class="tk-cap${level}"><i style="width: ${fill.percent}%"></i></div>
                        <div class="tk-grid">${grid}</div>
                    </section>
                    <section class="tk-sec tk-sec-scopes">
                        <div class="tk-h"><span>${esc(tk("inventory_scopes"))}</span></div>
                        <div class="tk-grid tk-scopes">${scopeTiles}</div>
                    </section>
                </div>
            </div>
            ${detail}`;
    }
}

export const InventoryScreen = new InventoryScreenClass();
